import axios from "axios";
import React, { useEffect, useState } from "react";
import { ApiUrl } from "../../services/helper";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setSearchOperation } from "../../redux/slice/QuickReportSlice";
import appStoreBadge from "../../Assets/Logo/appstore.png";
import googlePlayBadge from "../../Assets/Logo/googlePlay.png";
import tellArnieLogo from "../../Assets/Logo/TellArnie-Green-Logo.png";
import firstAidBox from "../../Assets/images/TellArnie_FirstAidBox_WHT.png";
import phoneMockup from "../../Assets/images/TellArnie-Android-Mockup.png";
import homeiPhone from "../../Assets/images/TellArnie-Home-iPhone.png";
import bellIllustration from "../../Assets/images/TellArnie-Bell.png";
import clipboardIcon from "../../Assets/images/TellArnie-Clipboard.png";
import registerKitIcon from "../../Assets/images/TellArnie-RegisterKit.png";
import scanBarcodeIcon from "../../Assets/images/TellArnie-ScanBarcode.png";
import GreenThemeLoader from "../../components/Common/Loader/GreenThemeLoader";

const BRAND_GREEN = "#359742";
const MARKETING_URL = "https://tellarnie.com";
const IOS_APP_URL = "https://apps.apple.com/us/app/tell-arnie/id6478534800";
const ANDROID_APP_URL =
  "https://play.google.com/store/apps/details?id=com.projectsetups";

const WithOutLoginData = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const res = await axios.get(`${ApiUrl}admin/fetch_qr_image/${id}`);
      if (res.status === 200) {
        dispatch(setSearchOperation(res.data));
        if (res.data?.kit_data && res.data?.kit_data?.company_id !== "") {
          navigate("/standardwithquickreport");
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Deep-link handling: if the Tell Arnie app is installed, open it directly;
  // otherwise fall back to showing this web landing page.
  useEffect(() => {
    const isMobile = /android|iphone|ipad|ipod/i.test(
      navigator.userAgent || ""
    );

    // On desktop there is no app to open — go straight to the web flow.
    if (!isMobile) {
      fetchData();
      return;
    }

    // The native app registers the "tellarnieapp://" custom scheme. We pass the
    // kit id so the app can route to the correct kit.
    const appSchemeUrl = `tellarnieapp://kit/${id}`;

    // If the OS switches to the app, this tab gets hidden — we detect that and
    // cancel the web fallback. If the page is still visible after the timeout,
    // the app is not installed, so we render the landing page.
    let appOpened = false;
    const handleVisibilityChange = () => {
      if (document.hidden) appOpened = true;
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const fallbackTimer = setTimeout(() => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (!appOpened && !document.hidden) {
        fetchData();
      }
    }, 1500);

    // Attempt to launch the app.
    window.location.href = appSchemeUrl;

    return () => {
      clearTimeout(fallbackTimer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: "100vh", backgroundColor: "#fff" }}
      >
        <GreenThemeLoader />
      </div>
    );
  }

  return (
    <div
      className="d-flex flex-column"
      style={{ minHeight: "100vh", backgroundColor: BRAND_GREEN }}
    >
      {/* White header bar with the green Tell Arnie logo (links to marketing site) */}
      <header
        className="bg-white w-100"
        style={{
          padding: "14px 24px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          position: "sticky",
          top: 0,
          zIndex: 1030,
        }}
      >
        <a
          href={MARKETING_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-block",
            outline: "none",
            border: "none",
            textDecoration: "none",
          }}
        >
          <img
            src={tellArnieLogo}
            alt="Tell Arnie"
            style={{
              height: "48px",
              width: "auto",
              border: "none",
              outline: "none",
            }}
          />
        </a>
      </header>

      {/* Hero */}
      <div
        className="flex-grow-1 d-flex align-items-center position-relative"
        style={{ overflow: "hidden" }}
      >
        {/* Faint first aid kit line illustration (decorative, hidden on small screens) */}
        <img
          src={firstAidBox}
          alt=""
          aria-hidden="true"
          className="d-none d-lg-block position-absolute"
          style={{
            left: "-40px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "360px",
            opacity: 0.85,
            pointerEvents: "none",
          }}
        />

        <div className="container py-5">
          <div className="row align-items-center g-5">
            {/* Text + store badges */}
            <div className="col-12 col-lg-7 text-center text-lg-start">
              <h1
                className="open-sans-font"
                style={{
                  color: "#A7D676",
                  fontWeight: 800,
                  fontSize: "clamp(34px, 6vw, 64px)",
                  lineHeight: 1.05,
                  marginBottom: 0,
                }}
              >
                Accident at work?...
              </h1>
              <h1
                className="open-sans-font"
                style={{
                  color: "#ffffff",
                  fontWeight: 800,
                  fontSize: "clamp(40px, 7vw, 72px)",
                  lineHeight: 1.05,
                  marginBottom: "24px",
                }}
              >
                ...Tell Arnie!
              </h1>

              <h4
                className="open-sans-font"
                style={{ color: "#fff", fontWeight: 700, marginBottom: "16px" }}
              >
                Your complete first aid kit management app.
              </h4>

              <p
                className="open-sans-font mx-auto mx-lg-0"
                style={{
                  color: "rgba(255,255,255,0.95)",
                  fontSize: "16px",
                  lineHeight: 1.6,
                  maxWidth: "520px",
                }}
              >
                Tell Arnie takes the pain out of managing workplace first aid.
                Tell Arnie provides Health and Safety Officers with full
                oversight and control over first aid kits to ensure they are
                complete, up-to-date and compliant with the law.
              </p>

              <div
                className="d-flex flex-wrap justify-content-center justify-content-lg-start align-items-center gap-3 mt-4"
              >
                <a href={IOS_APP_URL} target="_blank" rel="noopener noreferrer">
                  <img
                    src={appStoreBadge}
                    alt="Download on the App Store"
                    style={{ height: "52px", width: "auto" }}
                  />
                </a>
                <a
                  href={ANDROID_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={googlePlayBadge}
                    alt="Get it on Google Play"
                    style={{ height: "52px", width: "auto" }}
                  />
                </a>
              </div>
            </div>

            {/* Phone mockup */}
            <div className="col-12 col-lg-5 text-center">
              <img
                src={phoneMockup}
                alt="Tell Arnie app preview"
                style={{
                  width: "100%",
                  maxWidth: "320px",
                  height: "auto",
                  transform: "rotate(8deg)",
                  filter: "drop-shadow(0 18px 30px rgba(0,0,0,0.30))",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* "Keep in control" feature section */}
      <section
        className="position-relative"
        style={{ backgroundColor: "#8DC63F", overflow: "hidden" }}
      >
        {/* Decorative bell line illustration (hidden on small screens) */}
        <img
          src={bellIllustration}
          alt=""
          aria-hidden="true"
          className="d-none d-xl-block position-absolute"
          style={{
            right: "0px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "360px",
            opacity: 0.95,
            pointerEvents: "none",
          }}
        />

        <div className="container py-5">
          <div className="row align-items-center g-5">
            {/* iPhone mockup */}
            <div className="col-12 col-lg-5 text-center">
              <img
                src={homeiPhone}
                alt="Tell Arnie app home screen"
                style={{
                  width: "100%",
                  maxWidth: "330px",
                  height: "auto",
                  filter: "drop-shadow(0 18px 30px rgba(0,0,0,0.25))",
                }}
              />
            </div>

            {/* Copy */}
            <div className="col-12 col-lg-7">
              <h2
                className="open-sans-font"
                style={{
                  color: "#ffffff",
                  fontWeight: 800,
                  fontSize: "clamp(28px, 3.4vw, 44px)",
                  lineHeight: 1.15,
                  marginBottom: "20px",
                }}
              >
                Keep in control with Tell Arnie&rsquo;s incident tracking and
                compliance monitoring
              </h2>

              <p
                className="open-sans-font"
                style={{
                  color: "#1B5E20",
                  fontWeight: 700,
                  fontSize: "17px",
                  lineHeight: 1.5,
                  marginBottom: "24px",
                }}
              >
                Tell Arnie is the complete app for safety officers, providing
                real-time tracking of incidents, easy compliance monitoring, and
                refill alerts for British Standard first aid kits.
              </p>

              {[
                "makes keeping in control of your workplace first aid kits easy. Monitor usage across one or any number of sites at a glance with the Tell Arnie dashboard.",
                "is your digital accident book – quickly report and record an incident, scan your first aid kit and report which first aid products you have used.",
                "will notify you when kits are used, near expiry or expired and keep you up to date with important first aid news.",
              ].map((text, index) => (
                <div key={index} className="d-flex mb-3" style={{ gap: "12px" }}>
                  <span
                    aria-hidden="true"
                    style={{
                      color: "#1B5E20",
                      fontWeight: 800,
                      fontSize: "22px",
                      lineHeight: 1.3,
                    }}
                  >
                    +
                  </span>
                  <p
                    className="open-sans-font mb-0"
                    style={{
                      color: "#1B5E20",
                      fontSize: "16px",
                      lineHeight: 1.55,
                      maxWidth: "560px",
                    }}
                  >
                    <strong>Tell Arnie</strong> {text}
                  </p>
                </div>
              ))}

              <h3
                className="open-sans-font"
                style={{
                  color: "#1B5E20",
                  fontWeight: 800,
                  fontSize: "clamp(24px, 2.6vw, 34px)",
                  margin: "24px 0 10px",
                }}
              >
                It&rsquo;s the law!
              </h3>
              <p
                className="open-sans-font"
                style={{
                  color: "#ffffff",
                  fontWeight: 700,
                  fontSize: "16px",
                  lineHeight: 1.55,
                  maxWidth: "560px",
                }}
              >
                Keep compliant with the law by ensuring your workplace first aid
                kits are complete and up to date with Tell Arnie.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview video section — full-bleed, full screen */}
      <section
        style={{
          backgroundColor: BRAND_GREEN,
          width: "100%",
          height: "100vh",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <iframe
          src="https://www.youtube.com/embed/rZCHVTr7N7I?autoplay=1&mute=1&loop=1&playlist=rZCHVTr7N7I&rel=0&playsinline=1"
          title="Tell Arnie overview"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            border: 0,
          }}
        />
      </section>

      {/* "Track Incidents and Monitor Safety Kits with Ease" feature section */}
      <section style={{ backgroundColor: "#ffffff" }}>
        <div className="container py-5">
          <h2
            className="open-sans-font"
            style={{
              color: BRAND_GREEN,
              fontWeight: 800,
              fontSize: "clamp(28px, 3.6vw, 46px)",
              marginBottom: "20px",
            }}
          >
            Track Incidents and Monitor Safety Kits with Ease
          </h2>

          <p
            className="open-sans-font"
            style={{
              color: "#1B5E20",
              fontSize: "16px",
              lineHeight: 1.6,
              maxWidth: "1100px",
              marginBottom: "48px",
            }}
          >
            With <strong>Tell Arnie</strong>, Health and Safety Officers can
            quickly report an incident, scan and register a first aid kit,
            monitor its usage, and ensure that it is fully compliant with the
            law. Users have complete oversight and control of first aid
            throughout their organisation – whether this is just one workplace
            or any number of additional sites.
          </p>

          <div className="row text-center g-5">
            {[
              { img: clipboardIcon, label: "Efficient Incident Tracking" },
              { img: registerKitIcon, label: "Ensure Safety Kit Compliance" },
              { img: scanBarcodeIcon, label: "Streamlined Safety Management" },
            ].map((feature, index) => (
              <div key={index} className="col-12 col-md-4">
                <img
                  src={feature.img}
                  alt={feature.label}
                  style={{ width: "200px", maxWidth: "60%", height: "auto" }}
                />
                <p
                  className="open-sans-font mt-3 mb-0"
                  style={{
                    color: "#16161D",
                    fontWeight: 700,
                    fontSize: "16px",
                  }}
                >
                  {feature.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* "Transform your workplace safety" CTA section */}
      <section style={{ backgroundColor: BRAND_GREEN }}>
        <div className="container py-5 text-center">
          <h2
            className="open-sans-font"
            style={{
              color: "#A7D676",
              fontWeight: 800,
              fontSize: "clamp(34px, 5.5vw, 60px)",
              marginBottom: "20px",
            }}
          >
            Transform your workplace safety
          </h2>

          <p
            className="open-sans-font"
            style={{
              color: "#ffffff",
              fontSize: "20px",
              marginBottom: "28px",
            }}
          >
            <strong>Tell Arnie</strong> is free to download and use
          </p>

          <div className="d-flex flex-wrap justify-content-center align-items-center gap-3">
            <a href={IOS_APP_URL} target="_blank" rel="noopener noreferrer">
              <img
                src={appStoreBadge}
                alt="Download on the App Store"
                style={{ height: "56px", width: "auto" }}
              />
            </a>
            <a
              href={ANDROID_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={googlePlayBadge}
                alt="Get it on Google Play"
                style={{ height: "56px", width: "auto" }}
              />
            </a>
          </div>
        </div>
      </section>

      {/* "How to use Tell Arnie" section */}
      <section style={{ backgroundColor: "#ffffff" }}>
        <div className="container py-5 text-center">
          <h2
            className="open-sans-font"
            style={{
              color: BRAND_GREEN,
              fontWeight: 800,
              fontSize: "clamp(34px, 5vw, 56px)",
              marginBottom: "14px",
            }}
          >
            How to use Tell Arnie
          </h2>
          <p
            className="open-sans-font"
            style={{ color: "#1B5E20", fontSize: "18px", marginBottom: "36px" }}
          >
            Find out how <strong>Tell Arnie</strong> makes Workplace first aid
            kit management easy
          </p>

          <div className="row justify-content-center">
            <div className="col-12 col-lg-9">
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  paddingTop: "56.25%" /* 16:9 aspect ratio */,
                  overflow: "hidden",
                  boxShadow: "0 18px 40px rgba(0,0,0,0.20)",
                }}
              >
                <iframe
                  src="https://www.youtube.com/embed/_HTPyZh3v6s?rel=0"
                  title="How to use Tell Arnie – Registration"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: 0,
                  }}
                />
              </div>
            </div>
          </div>

          <p
            className="open-sans-font mt-4 mb-0"
            style={{ color: "#1B5E20", fontSize: "16px", lineHeight: 1.6 }}
          >
            <strong>Tell Arnie</strong> provides Health and Safety Officers full
            oversight and control over workplace first aid kits.
            <br />
            Monitor, report, and manage first aid supplies effortlessly to
            ensure compliance with safety regulations.
          </p>
        </div>
      </section>
    </div>
  );
};

export default WithOutLoginData;
